import Promise from 'bluebird';
import $ from 'jquery';
import _ from 'underscore';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { DeterministicView, helpers } from '@bingads-webui/deterministic-view';
import { BidCapPanelView } from './bid-cap-panel';
import { BudgetPanelView } from './budget-panel';
import templateUINext from './subaccount-bar-ui-next-template.pug';
import SubAccountBidEdit from './subaccount-bid-edit';
import SubAccountBidMultipliersEdit from './subaccount-bid-multipliers-edit';
import { instrumentation, SubAccountModel } from '../hotel-center-shared';
import { bidMultiplierUtils } from '../hotel-center-utils';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/hotel-center');
const edm = globalContext.requireDefault('campaignui/feature/hotel-center-edm/index');
const jsdata = globalContext.require('campaignui/feature/hotel-center-js-data/index');
const config = globalContext.require('component/config/index');
const decimal = globalContext.require('component/humanize/decimal');
const state = globalContext.require('component/state/index');

export {
  BidCapPanelView,
  BudgetPanelView,
  SubAccountBidEdit,
  SubAccountBidMultipliersEdit,
};

/**
 * Class representing a navigation tabs for SubAccount Bar
 * @extends View
 */
export class SubAccountBar extends DeterministicView {
  /**
   * Initializes SubAccountBar
   * @returns {undefined}
   */
  initialize() {
    this.i18n = i18n;
    this.subAccountBarScenario = instrumentation.subAccountBar.create();

    const loadSubAccountInfoActivity = this.subAccountBarScenario.loadSubAccountInfo.create();

    this.subAccountModel = new SubAccountModel();
    loadSubAccountInfoActivity.monitorAsync(this.subAccountModel.fetch())
      .then(() => {
        const subAccountName = state.get('subAccount_model') && state.get('subAccount_model').Name;
        $('.visible-element .summary-bar-breadcrumb .current-breadcrumb').removeClass('current-breadcrumb');
        $('.visible-element .summary-bar-breadcrumb+.summary-bar-divider').addClass('hide');
        helpers.updateView(this, {
          defaultBid: this.getBidText(),
          defaultDeviceTypesWithMultipliers: this.formatSummaryBarBidMultiplier(bidMultiplierUtils.getMultipliersText(this.subAccountModel.get('BidMultipliers'), false)),
          hotelTotalCount: decimal.fromLocalToString(this.subAccountModel.get('HotelAssociationCount'), 0),
          subAccountName,
        });
      });

    this.fetchHotelGroups(state.get('subAccount_id'));

    this.subAccountBidEdit = new SubAccountBidEdit({ subAccountModel: this.subAccountModel });
    this.setChild('subaccount-bar-edit', this.subAccountBidEdit);

    this.subAccountBidMultipliersEdit = new SubAccountBidMultipliersEdit({
      subAccountModel: this.subAccountModel,
    });
    this.setChild('subaccount-multipliers-bar-edit', this.subAccountBidMultipliersEdit);

    this.listenTo(this.subAccountBidEdit, 'bid-changed', (value) => {
      if (value) {
        helpers.updateView(this, {
          defaultBid: value,
        });

        this.trigger('subaccountChanged', ['HotelIdGrid', 'HotelGroupGrid', 'HotelSubAccountGrid']);
      }
    });

    this.listenTo(this.subAccountBidMultipliersEdit, 'bid-multipliers-changed', (value) => {
      let defaultDeviceTypesWithMultipliers = value;
      defaultDeviceTypesWithMultipliers =
        this.formatSummaryBarBidMultiplier(defaultDeviceTypesWithMultipliers);
      helpers.updateView(this, {
        defaultDeviceTypesWithMultipliers,
      });
      this.trigger('subaccountChanged', ['HotelIdGrid', 'HotelGroupGrid', 'HotelSubAccountGrid']);
    });
    helpers.updateView(this, {
      hotelTotalCount: '   -',
      hotelGroupTotalCount: '   -',
      subAccountName: '   -',
    });

    const budgetPanelView = new BudgetPanelView();

    this.setChild('budget-panel', budgetPanelView);

    const bidCapPanelView = new BidCapPanelView();

    this.setChild('bid-cap-panel', bidCapPanelView);
  }

  get events() {
    return {
      'click .account-default-bid-edit': this.onAccountDefaultBidEdit,
      'click .account-default-bid-multipliers-edit': this.onAccountDefaultMultipliersBidEdit,
    };
  }

  formatSummaryBarBidMultiplier(bidMultipliers) {
    if (bidMultipliers) {
      const multipliers = bidMultipliers.split(',');

      return multipliers.length > 1 ? this.i18n.getString('{{firstMultiplier}} and {{multiplierCount}} more', { firstMultiplier: multipliers[0], multiplierCount: multipliers.length - 1 }) : bidMultipliers;
    }
    return bidMultipliers;
  }

  onAccountDefaultBidEdit(e) {
    // Since the event will bubble up to the light dismiss manager, it should specify
    // not to dismiss the target element
    e.originalEvent.noDismiss = 'subaccount-bid-edit-panel'; // eslint-disable-line no-param-reassign
    this.getChild('subaccount-bar-edit').toggleVisibility();
  }

  onAccountDefaultMultipliersBidEdit(e) {
    // Since the event will bubble up to the light dismiss manager, it should specify
    // not to dismiss the target element
    e.originalEvent.noDismiss = 'subaccount-bid-multipliers-edit-container'; // eslint-disable-line no-param-reassign

    this.getChild('subaccount-multipliers-bar-edit').toggleVisibility();
  }

  onSubAccountBarChanged() {
    // update subaccount bar when inline edit or other operations changed subaccount model
    this.subAccountModel.fetch().then(() => {
      helpers.updateView(this, {
        defaultBid: this.getBidText(),
        defaultDeviceTypesWithMultipliers: this.formatSummaryBarBidMultiplier(bidMultiplierUtils.getMultipliersText(this.subAccountModel.get('BidMultipliers'), false)),
      });
      this.getChild('subaccount-bar-edit').updateBidStrategyView();
      this.getChild('subaccount-multipliers-bar-edit').updateBidMultipliersView();
    });

    const loadSubAccountInfoActivity = this.subAccountBarScenario.loadSubAccountInfo.create();

    loadSubAccountInfoActivity.trace('Sub-account info is loaded');
  }

  get template() {
    return templateUINext;
  }

  getBidText() {
    if (!this.subAccountModel.get('Bid')) {
      return '';
    }

    if (this.subAccountModel.get('Bid')['@odata.type'] !== '#Model.FixedBid') {
      return `${i18n.formatDecimalToFixed(this.subAccountModel.get('Bid').Amount)} ${decimal.getFormat().percentSymbol}`;
    }
    return `${i18n.formatDecimalToFixed(this.subAccountModel.get('Bid').Amount)} ${config.get('AccountCurrencyCode')}`;
  }

  fetchHotelGroups(subAccountId) {
    const hotelGroupCount = state.get('subAccount_hotelGroupCount');
    let groupCountLoader;

    if (_.result(hotelGroupCount, 'Id') === subAccountId && _.has(hotelGroupCount, 'totalCount')) {
      groupCountLoader = new Promise((resolve) => {
        resolve(hotelGroupCount.totalCount);
      });
    } else if (subAccountId === '') {
      groupCountLoader = new Promise((resolve) => {
        resolve(0);
      });
    } else {
      const entitySet = edm.currentAccount.SubAccounts.$withKey(subAccountId).HotelGroups;

      groupCountLoader = jsdata.HotelGroup
        .findAll({}, {
          entitySet,
          cacheResponse: false,
        })
        .then((hotelGroups) => {
          state.set('subAccount_hotelGroupCount', {
            Id: subAccountId,
            totalCount: hotelGroups.totalCount,
          });
          return hotelGroups.totalCount;
        });
    }

    groupCountLoader.then((totalCount) => {
      helpers.updateView(this, {
        hotelGroupTotalCount: decimal.fromLocalToString(totalCount, 0),
      });
    });
  }
}
